import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Box, CssBaseline, Drawer, IconButton, List,
  ListItem, ListItemButton, ListItemIcon, ListItemText, Toolbar,
  Typography, Avatar, Tooltip, Container, Breadcrumbs, Link,
  useMediaQuery, InputBase,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  DashboardCustomize as DashboardIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Home as HomeIcon,
  Logout as LogoutIcon,
  Public as PublicIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { toStorageUrl } from '../../utils/storageUrl';
import { canAccessPartnershipManagement } from '../../utils/partnershipPermissions';
import { canAccessAttendanceLogs } from '../../utils/attendancePermissions';
import { NAV_ICON_BY_PATH } from '../../utils/navIcons';

const NavIcon = ({ path }) => {
  const Icon = NAV_ICON_BY_PATH[path];
  return Icon ? <Icon /> : <DashboardIcon />;
};

// ─── Constants ────────────────────────────────────────────────────────────────
const DRAWER_WIDTH  = 240;
const COLLAPSED_W   = 60;
const CLUB_LOGO_SRC = '/picture/LogoClub/UTMRBCLOGO.png';

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = {
  bg0:    '#03080F',
  bg1:    '#060D1A',
  bg2:    '#080F1F',
  bg3:    '#0C1528',
  bg4:    '#111D35',
  neon:   '#00BAFF',
  blue:   '#3A8EFF',
  indigo: '#5A6FFF',
  teal:   '#00E5C0',
  violet: '#7A60FF',
  ok:     '#00D49A',
  t0:     '#E0F2FF',
  t1:     '#A8CCEE',
  t2:     '#607EA0',
  t3:     '#2E4A65',
  a: (hex, op) => alpha(hex, op),
};

const SECTION_COLORS = {
  'Overview':     C.neon,
  'Personnel':    C.blue,
  'Organization': C.indigo,
  'Operations':   C.teal,
  'Media':        C.violet,
  'Partnership':  C.blue,
  'Game':         C.neon,
  'Inventory':    C.teal,
  'Finance':      C.blue,
  'Documents':    C.indigo,
  'Maintenance':  C.violet,
};

// ─── Global styles ────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; }

  html, body {
    background: ${C.bg0} !important;
    font-family: 'DM Sans', sans-serif;
    color: ${C.t0};
    -webkit-font-smoothing: antialiased;
    overflow-x: hidden;
  }

  ::-webkit-scrollbar { width: 2px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: ${C.t3}; border-radius: 99px; }

  @keyframes pulse-dot {
    0%,100% { opacity:1; transform:scale(1); }
    50%      { opacity:.4; transform:scale(.65); }
  }
  @keyframes spin-ring {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  @keyframes flow-border {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  @media (max-width: 600px) {
    .utm-breadcrumbs .MuiBreadcrumbs-ol {
      flex-wrap: nowrap;
      overflow: hidden;
    }
    .utm-breadcrumbs .MuiBreadcrumbs-li {
      min-width: 0;
    }
    .utm-breadcrumbs .MuiBreadcrumbs-li:last-child {
      flex-shrink: 1;
    }
  }
`;

// ─── Logo ──────────────────────────────────────────────────────────────────────
const Logo = ({ size = 32 }) => (
  <Box sx={{ position: 'relative', width: size + 6, height: size + 6, flexShrink: 0 }}>
    <Box sx={{
      position: 'absolute', inset: 0, borderRadius: '50%',
      background: `conic-gradient(${C.neon}, ${C.blue}, ${C.indigo}, ${C.neon})`,
      animation: 'spin-ring 4s linear infinite',
      opacity: 0.7,
    }} />
    <Box sx={{
      position: 'absolute', inset: 2, borderRadius: '50%',
      background: C.bg2,
      display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
    }}>
      <Box component="img" src={CLUB_LOGO_SRC} alt=""
        sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0.4 }} />
    </Box>
  </Box>
);

// ─── Section divider ──────────────────────────────────────────────────────────
const SectionDivider = ({ label, color, collapsed }) => (
  collapsed
    ? <Box sx={{ mx: 1.5, my: 1, height: '1px', background: alpha(color, 0.15) }} />
    : <Box sx={{ px: 2, pt: 1.5, pb: 0.25, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{
          fontFamily: "'DM Mono', monospace",
          fontSize: '0.46rem', letterSpacing: 3, textTransform: 'uppercase',
          color: alpha(color, 0.45), whiteSpace: 'nowrap',
        }}>{label}</Typography>
        <Box sx={{ flex: 1, height: '1px', background: alpha(color, 0.12) }} />
      </Box>
);

// ─── Active bar ────────────────────────────────────────────────────────────────
const ActiveBar = ({ color }) => (
  <Box sx={{
    position: 'absolute', left: 0, top: '18%', bottom: '18%',
    width: 2.5, borderRadius: '0 2px 2px 0',
    background: color,
    boxShadow: `0 0 8px ${alpha(color, 0.8)}`,
  }} />
);

const NavDrawerSearch = ({ value, onChange, inputRef, onClear }) => (
  <Box sx={{ px: 1.5, pb: 1 }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1,
      px: 1.25, py: 0.75,
      borderRadius: '10px',
      border: `1px solid ${alpha(C.neon, 0.22)}`,
      background: alpha(C.bg3, 0.65),
      transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
      '&:focus-within': {
        borderColor: alpha(C.neon, 0.45),
        boxShadow: `0 0 0 2px ${alpha(C.neon, 0.1)}`,
      },
    }}>
      <SearchIcon sx={{ fontSize: 17, color: alpha(C.t2, 0.75), flexShrink: 0 }} />
      <InputBase
        inputRef={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search menu…"
        inputProps={{ 'aria-label': 'Search navigation menu' }}
        sx={{
          flex: 1, minWidth: 0,
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '0.875rem',
          color: C.t0,
          '& input::placeholder': { color: alpha(C.t2, 0.55), opacity: 1 },
        }}
      />
      {value ? (
        <IconButton
          size="small"
          onClick={onClear}
          aria-label="Clear search"
          sx={{
            width: 24, height: 24, color: alpha(C.t2, 0.7),
            '&:hover': { color: C.t0, background: alpha(C.neon, 0.08) },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      ) : null}
    </Box>
    {!value && (
      <Typography sx={{
        mt: 0.75, px: 0.25,
        fontFamily: "'DM Mono', monospace",
        fontSize: '0.52rem', letterSpacing: 0.8,
        color: alpha(C.t2, 0.55),
      }}>
        Filter menu by page or section
      </Typography>
    )}
  </Box>
);

// ═════════════════════════════════════════════════════════════════════════════
// MAIN LAYOUT
// ═════════════════════════════════════════════════════════════════════════════
const InternalLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed,  setCollapsed]  = useState(false);
  const [navSearchQuery, setNavSearchQuery] = useState('');

  const navSearchInputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const id = 'utm-layout-v2';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id; el.innerHTML = GLOBAL_CSS;
      document.head.appendChild(el);
    }
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setNavSearchQuery('');
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) {
      setNavSearchQuery('');
      return undefined;
    }
    const timer = setTimeout(() => navSearchInputRef.current?.focus(), 120);
    return () => clearTimeout(timer);
  }, [mobileOpen]);

  // ── Role flags ─────────────────────────────────────────────────────────────
  const isAdmin        = Boolean(user?.is_admin);
  const isTL           = Boolean(user?.is_team_leader) || user?.management_id === 1;
  const isTreasurer    = user?.management_id === 3;
  const isInventory    = user?.bureau_id === 6;
  const isInactiveUser = String(user?.status || 'active').toLowerCase() === 'inactive';

  const hasGenDept = isAdmin;
  const hasBudget  = isTL || isTreasurer;
  const hasPartner = canAccessPartnershipManagement(user);
  const hasInv     = isTL || isInventory;

  // ── Menu ───────────────────────────────────────────────────────────────────
  const menuSections = isInactiveUser
    ? [{ title: 'Media', items: [
        { text: 'Gallery', icon: <NavIcon path="/galleries" />, path: '/galleries' },
      ]}]
    : isAdmin
    ? [
        { title: 'Overview', items: [
          { text: 'Accessibility', icon: <NavIcon path="/accessibility-view" />, path: '/accessibility-view' },
        ]},
        { title: 'Personnel', items: [
          { text: 'Users', icon: <NavIcon path="/users" />, path: '/users' },
        ]},
        ...(hasGenDept ? [{ title: 'Organization', items: [
          { text: 'Management', icon: <NavIcon path="/managements" />, path: '/managements' },
          { text: 'Bureau',     icon: <NavIcon path="/bureaus" />,     path: '/bureaus'     },
          { text: 'Department', icon: <NavIcon path="/departments" />, path: '/departments' },
        ]}] : []),
      ]
    : [
        { title: 'Overview', items: [
          { text: 'Dashboard', icon: <NavIcon path="/dashboard" />, path: '/dashboard' },
        ]},
        { title: 'Personnel', items: [
          { text: 'Users',      icon: <NavIcon path="/users" />,      path: '/users'      },
          { text: 'Attendance', icon: <NavIcon path="/attendance" />, path: '/attendance' },
          ...(canAccessAttendanceLogs(user) ? [{
            text: 'Attendance Logs',
            icon: <NavIcon path="/attendance/logs" />,
            path: '/attendance/logs',
          }] : []),
        ]},
        ...(hasGenDept ? [{ title: 'Organization', items: [
          { text: 'Management', icon: <NavIcon path="/managements" />, path: '/managements' },
          { text: 'Bureau',     icon: <NavIcon path="/bureaus" />,     path: '/bureaus'     },
          { text: 'Department', icon: <NavIcon path="/departments" />, path: '/departments' },
        ]}] : []),
        { title: 'Operations', items: [
          { text: 'Calendar',  icon: <NavIcon path="/calendar" />,  path: '/calendar'  },
          { text: 'Events',    icon: <NavIcon path="/events" />,    path: '/events'    },
          { text: 'Penalties', icon: <NavIcon path="/penalties" />, path: '/penalties' },
          { text: 'Tasks',     icon: <NavIcon path="/tasks" />,     path: '/tasks'     },
        ]},
        { title: 'Media', items: [
          { text: 'Gallery', icon: <NavIcon path="/galleries" />, path: '/galleries' },
        ]},
        { title: 'Partnership', items: [
          { text: 'Requirements', icon: <NavIcon path="/requirements" />, path: '/requirements' },
          ...(hasPartner ? [
            { text: 'Companies',     icon: <NavIcon path="/companies" />,     path: '/companies'     },
            { text: 'Partners',      icon: <NavIcon path="/partners" />,      path: '/partners'      },
            { text: 'Sponsor Tiers', icon: <NavIcon path="/sponsor-tiers" />, path: '/sponsor-tiers' },
          ] : []),
        ]},
        { title: 'Game', items: [
          { text: 'Analysis', icon: <NavIcon path="/analyses" />, path: '/analyses' },
        ]},
        ...(hasInv ? [{ title: 'Inventory', items: [
          { text: 'Inventory', icon: <NavIcon path="/stocks" />,  path: '/stocks'  },
          { text: 'Borrow', icon: <NavIcon path="/borrows" />, path: '/borrows' },
        ]}] : []),
        { title: 'Finance', items: [
          { text: 'Parcels', icon: <NavIcon path="/parcels" />, path: '/parcels' },
          { text: 'Claims',  icon: <NavIcon path="/claims" />,  path: '/claims'  },
          ...(hasBudget ? [
            { text: 'Budget',   icon: <NavIcon path="/budgets" />,         path: '/budgets'         },
            { text: 'Expenses', icon: <NavIcon path="/budget-expenses" />, path: '/budget-expenses' },
          ] : []),
        ]},
        { title: 'Documents', items: [
          { text: 'Documents', icon: <NavIcon path="/documents" />, path: '/documents' },
        ]},
        { title: 'Maintenance', items: [
          { text: 'Maintenance', icon: <NavIcon path="/maintenances" />, path: '/maintenances' },
        ]},
      ];

  const visibleMenuSections = useMemo(() => {
    const query = navSearchQuery.trim().toLowerCase();
    if (!query) return menuSections;

    return menuSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) =>
          item.text.toLowerCase().includes(query)
          || section.title.toLowerCase().includes(query)
          || item.path.toLowerCase().includes(query)
        ),
      }))
      .filter((section) => section.items.length > 0);
  }, [menuSections, navSearchQuery]);

  const allPaths = menuSections.flatMap(s => s.items.map(i => i.path));
  const activeMenuPath = [...allPaths]
    .sort((a, b) => b.length - a.length)
    .find(p => location.pathname === p || location.pathname.startsWith(`${p}/`));

  const activeSection = menuSections.find(s =>
    s.items.some(i => location.pathname === i.path || location.pathname.startsWith(`${i.path}/`))
  );
  const activeColor = SECTION_COLORS[activeSection?.title] ?? C.neon;

  const breadcrumbs = useMemo(() => {
    const segs = location.pathname.split('/').filter(Boolean);
    return [
      { label: 'Home', path: '/dashboard' },
      ...segs.map((s, i) => ({
        label: s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' '),
        path:  '/' + segs.slice(0, i + 1).join('/'),
      })),
    ];
  }, [location.pathname]);

  const displayBreadcrumbs = useMemo(() => {
    if (!isMobile || breadcrumbs.length <= 2) return breadcrumbs;
    return [
      breadcrumbs[0],
      { label: '…', path: null, ellipsis: true },
      breadcrumbs[breadcrumbs.length - 1],
    ];
  }, [breadcrumbs, isMobile]);

  const handleNavClick = useCallback((path) => {
    navigate(path);
    setMobileOpen(false);
    setNavSearchQuery('');
  }, [navigate]);

  const handleMobileDrawerClose = useCallback(() => {
    setMobileOpen(false);
    setNavSearchQuery('');
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login', { replace: true });
    window.location.reload();
  };

  const handleGoToPublicSite = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const appBarIconBtnSx = {
    width: 32,
    height: 32,
    borderRadius: '8px',
    color: C.t3,
    border: `1px solid ${alpha(C.t3, 0.2)}`,
  };

  const W = collapsed ? COLLAPSED_W : DRAWER_WIDTH;

  // ─── Drawer content ────────────────────────────────────────────────────────
  const renderDrawerContent = (drawerCollapsed, { mobileMode = false } = {}) => {
    const sectionsToRender = mobileMode ? visibleMenuSections : menuSections;
    const searchActive = mobileMode && navSearchQuery.trim().length > 0;

    return (
    <Box sx={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: C.bg1,
      borderRight: `1px solid ${alpha(C.neon, 0.08)}`,
    }}>

      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', minHeight: 56, px: drawerCollapsed ? 0 : 2,
        justifyContent: drawerCollapsed ? 'center' : 'space-between',
        borderBottom: `1px solid ${alpha(C.neon, 0.08)}`,
        position: 'relative',
        '&::after': {
          content: '""', position: 'absolute', bottom: 0, left: '10%', right: '10%', height: '1px',
          background: `linear-gradient(90deg, transparent, ${C.neon}, transparent)`,
          backgroundSize: '200% 100%',
          animation: 'flow-border 4s ease infinite',
          opacity: 0.4,
        },
      }}>
        <AnimatePresence mode="wait">
          {!drawerCollapsed ? (
            <motion.div key="full"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              style={{ display: 'flex', alignItems: 'center', gap: 10 }}
            >
              <Logo size={28} />
              <Box>
                <Typography sx={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '0.82rem', fontWeight: 500, letterSpacing: 1.2,
                  color: C.neon, lineHeight: 1,
                }}>UTM Robocon</Typography>
                <Typography sx={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '0.44rem', letterSpacing: 2.5, mt: 0.4,
                  color: alpha(C.t2, 0.6), textTransform: 'uppercase',
                }}>Control System</Typography>
              </Box>
            </motion.div>
          ) : (
            <motion.div key="icon"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <Logo size={26} />
            </motion.div>
          )}
        </AnimatePresence>

        {!drawerCollapsed && (
          <IconButton onClick={() => (mobileMode ? handleMobileDrawerClose() : setCollapsed(true))}
            sx={{
              display: { xs: mobileMode ? 'flex' : 'none', sm: mobileMode ? 'none' : 'flex' },
              width: 24, height: 24, borderRadius: '6px',
              color: C.t3, border: `1px solid ${alpha(C.t3, 0.2)}`,
              '&:hover': { color: C.neon, borderColor: alpha(C.neon, 0.35), background: alpha(C.neon, 0.06) },
            }}>
            {mobileMode ? <CloseIcon sx={{ fontSize: 14 }} /> : <ChevronLeftIcon sx={{ fontSize: 14 }} />}
          </IconButton>
        )}
        {drawerCollapsed && (
          <Box sx={{ position: 'absolute', bottom: -12, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
            <IconButton onClick={() => setCollapsed(false)}
              sx={{
                display: { xs: 'none', sm: 'flex' },
                width: 22, height: 22, borderRadius: '50%',
                color: C.t2, border: `1px solid ${alpha(C.neon, 0.3)}`,
                background: C.bg2,
                '&:hover': { color: C.neon, background: C.bg3 },
              }}>
              <ChevronRightIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* User chip */}
      <Box sx={{ px: drawerCollapsed ? 0.75 : 1.5, py: 1.25 }}>
        <Box onClick={() => handleNavClick('/profile')} sx={{
          display: 'flex', alignItems: 'center',
          gap: drawerCollapsed ? 0 : 1.25,
          justifyContent: drawerCollapsed ? 'center' : 'flex-start',
          px: drawerCollapsed ? 0 : 1.25, py: 0.9,
          borderRadius: '10px', cursor: 'pointer',
          border: `1px solid ${alpha(C.neon, 0.1)}`,
          background: alpha(C.bg3, 0.5),
          transition: 'all 0.18s ease',
          '&:hover': {
            background: alpha(C.bg4, 0.8),
            borderColor: alpha(C.neon, 0.25),
          },
        }}>
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Avatar
              src={toStorageUrl(user?.profile_picture || user?.profile_picture_url) || undefined}
              sx={{
                width: 28, height: 28, fontSize: '0.7rem', fontWeight: 600,
                background: `linear-gradient(135deg, ${C.neon}, ${C.indigo})`,
                border: `1.5px solid ${alpha(C.neon, 0.3)}`,
              }}>
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
            <Box sx={{
              position: 'absolute', bottom: -1, right: -1,
              width: 7, height: 7, borderRadius: '50%',
              background: C.ok, border: `1.5px solid ${C.bg2}`,
              animation: 'pulse-dot 2.5s ease-in-out infinite',
            }} />
          </Box>
          {!drawerCollapsed && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{
                fontSize: '0.8rem', fontWeight: 500, color: C.t0,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{user?.name || 'User'}</Typography>
              <Typography sx={{
                fontFamily: "'DM Mono', monospace",
                fontSize: '0.52rem', color: alpha(C.neon, 0.5), letterSpacing: 0.2,
              }}>profile →</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {mobileMode && !drawerCollapsed && (
        <NavDrawerSearch
          value={navSearchQuery}
          onChange={setNavSearchQuery}
          inputRef={navSearchInputRef}
          onClear={() => setNavSearchQuery('')}
        />
      )}

      {/* Nav */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: 0.75, pb: 1 }}>
        {mobileMode && searchActive && sectionsToRender.length === 0 ? (
          <Box sx={{ px: 1.5, py: 3, textAlign: 'center' }}>
            <Typography sx={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '0.875rem', color: alpha(C.t2, 0.75),
            }}>
              No pages match &ldquo;{navSearchQuery.trim()}&rdquo;
            </Typography>
          </Box>
        ) : (
        <List disablePadding>
          {sectionsToRender.map((section, si) => {
            const sc = SECTION_COLORS[section.title] ?? C.neon;
            const showSectionHeader = !drawerCollapsed && (
              !mobileMode || !searchActive || sectionsToRender.length > 1
            );
            return (
              <Box key={`${section.title}-${si}`}>
                {showSectionHeader && (
                  <SectionDivider label={section.title} color={sc} collapsed={drawerCollapsed} />
                )}

                {section.items.map(item => {
                  const active = activeMenuPath === item.path;
                  return (
                    <ListItem key={item.text} disablePadding sx={{ mb: 0.15 }}>
                      <Tooltip title={drawerCollapsed ? item.text : ''} placement="right" arrow
                        componentsProps={{ tooltip: { sx: {
                          background: C.bg4, color: C.t0,
                          fontSize: '0.78rem', borderRadius: '8px', px: 1.25, py: 0.6,
                          border: `1px solid ${alpha(sc, 0.25)}`,
                          fontFamily: "'DM Sans', sans-serif",
                        }}}}>
                        <ListItemButton
                          onClick={() => handleNavClick(item.path)}
                          sx={{
                            borderRadius: '9px', mx: 0.25, minHeight: { xs: 44, sm: 38 },
                            px: drawerCollapsed ? 0 : 1.25,
                            justifyContent: drawerCollapsed ? 'center' : 'flex-start',
                            position: 'relative',
                            background: active ? alpha(sc, 0.08) : 'transparent',
                            '& .nav-icon': {
                              color: active ? sc : C.t3,
                              fontSize: 20,
                              transition: 'color 0.15s ease',
                            },
                            '& .nav-label': {
                              color: active ? C.t0 : C.t2,
                              fontWeight: active ? 500 : 400,
                              fontSize: '0.875rem',
                              transition: 'color 0.15s ease',
                            },
                            '&:hover': {
                              background: alpha(sc, 0.06),
                              '& .nav-icon': { color: sc },
                              '& .nav-label': { color: C.t1 },
                            },
                            transition: 'background 0.15s ease',
                          }}
                        >
                          {active && <ActiveBar color={sc} />}

                          <ListItemIcon sx={{
                            minWidth: drawerCollapsed ? 0 : 30,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }} className="nav-icon">
                            {item.icon}
                          </ListItemIcon>

                          {!drawerCollapsed && (
                            <ListItemText
                              primary={item.text}
                              secondary={mobileMode && searchActive ? section.title : undefined}
                              primaryTypographyProps={{ className: 'nav-label' }}
                              secondaryTypographyProps={{
                                sx: {
                                  fontFamily: "'DM Mono', monospace",
                                  fontSize: '0.58rem',
                                  letterSpacing: 0.6,
                                  color: alpha(sc, 0.55),
                                  textTransform: 'uppercase',
                                },
                              }}
                              sx={{ my: 0 }}
                            />
                          )}
                        </ListItemButton>
                      </Tooltip>
                    </ListItem>
                  );
                })}
              </Box>
            );
          })}
        </List>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{
        px: drawerCollapsed ? 1 : 2, py: 1,
        borderTop: `1px solid ${alpha(C.neon, 0.07)}`,
        display: 'flex', alignItems: 'center',
        justifyContent: drawerCollapsed ? 'center' : 'space-between',
      }}>
        {!drawerCollapsed ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{
                width: 5, height: 5, borderRadius: '50%',
                background: C.ok, boxShadow: `0 0 6px ${C.ok}`,
                animation: 'pulse-dot 3s ease-in-out infinite',
              }} />
              <Typography sx={{
                fontFamily: "'DM Mono', monospace",
                fontSize: '0.5rem', letterSpacing: 2, textTransform: 'uppercase',
                color: alpha(C.ok, 0.55),
              }}>Online</Typography>
            </Box>
            <Typography sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: '0.48rem', color: C.t3, letterSpacing: 0.5,
            }}>v2.0.0</Typography>
          </>
        ) : (
          <Box sx={{
            width: 6, height: 6, borderRadius: '50%',
            background: C.ok, boxShadow: `0 0 8px ${C.ok}`,
            animation: 'pulse-dot 3s ease-in-out infinite',
          }} />
        )}
      </Box>
    </Box>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', background: C.bg0, minHeight: '100vh' }}>
      <CssBaseline />

      {/* Subtle dot grid background */}
      <Box sx={{
        position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
        backgroundImage: `radial-gradient(${alpha(C.neon, 0.07)} 1px, transparent 1px)`,
        backgroundSize: '28px 28px',
        maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black, transparent)',
      }} />

      {/* AppBar */}
      <AppBar position="fixed" elevation={0} sx={{
        width: { xs: '100%', sm: `calc(100% - ${W}px)` },
        ml: { xs: 0, sm: `${W}px` },
        background: alpha(C.bg1, 0.88),
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${alpha(C.neon, 0.08)}`,
        color: C.t0,
        transition: 'width 0.2s ease, margin 0.2s ease',
      }}>
        <Toolbar sx={{
          px: { xs: 1.5, sm: 2.5 },
          minHeight: { xs: '52px !important', sm: '56px !important' },
          gap: { xs: 1, sm: 1.5 },
        }}>

          <IconButton onClick={() => setMobileOpen(true)} sx={{
            display: { sm: 'none' }, color: C.t2,
            border: `1px solid ${alpha(C.neon, 0.2)}`, borderRadius: '8px',
            width: 34, height: 34,
            '&:hover': { color: C.neon, background: alpha(C.neon, 0.07) },
          }}>
            <MenuIcon sx={{ fontSize: 16 }} />
          </IconButton>

          {/* Breadcrumbs */}
          <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }} className="utm-breadcrumbs">
            <Breadcrumbs
              maxItems={isMobile ? 3 : 8}
              separator={<Box sx={{ width: 3, height: 3, borderRadius: '50%', background: alpha(C.t3, 0.5), flexShrink: 0 }} />}
            >
              {displayBreadcrumbs.map((crumb, i) => {
                const isLast = i === displayBreadcrumbs.length - 1;
                const isEllipsis = Boolean(crumb.ellipsis);
                return (
                  <Link key={`${crumb.label}-${i}`} underline="none"
                    onClick={e => {
                      e.preventDefault();
                      if (!isLast && !isEllipsis && crumb.path) navigate(crumb.path);
                    }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.4,
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: { xs: '0.78rem', sm: '0.84rem' },
                      fontWeight: isLast ? 500 : 400,
                      color: isLast ? C.neon : alpha(C.t2, 0.7),
                      cursor: isLast || isEllipsis ? 'default' : 'pointer',
                      transition: 'color 0.15s ease',
                      minWidth: 0,
                      maxWidth: isLast ? '100%' : 'none',
                      '& .utm-crumb-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                      '&:hover': !isLast && !isEllipsis ? { color: C.t1 } : {},
                    }}>
                    {i === 0 && !isEllipsis && <HomeIcon sx={{ fontSize: 11, flexShrink: 0 }} />}
                    <span className="utm-crumb-label">{crumb.label}</span>
                  </Link>
                );
              })}
            </Breadcrumbs>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
            <Tooltip title="Public website" placement="bottom">
              <IconButton
                onClick={handleGoToPublicSite}
                aria-label="Go to public website"
                sx={{
                  ...appBarIconBtnSx,
                  '&:hover': {
                    color: C.neon,
                    borderColor: alpha(C.neon, 0.35),
                    background: alpha(C.neon, 0.08),
                  },
                }}
              >
                <PublicIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Sign out" placement="bottom">
              <IconButton
                onClick={handleLogout}
                aria-label="Sign out"
                sx={{
                  ...appBarIconBtnSx,
                  '&:hover': {
                    color: C.t1,
                    borderColor: alpha(C.t2, 0.35),
                    background: alpha(C.t3, 0.1),
                  },
                }}
              >
                <LogoutIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box component="nav" sx={{ width: { sm: W }, flexShrink: { sm: 0 }, transition: 'width 0.2s ease' }}>
        <Drawer variant="temporary" open={mobileOpen}
          onClose={handleMobileDrawerClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              width: 'min(100vw - 24px, 300px)',
              maxWidth: '100%',
              border: 'none',
              background: C.bg1,
            },
          }}>
          {renderDrawerContent(false, { mobileMode: true })}
        </Drawer>

        <Drawer variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: W, overflowX: 'hidden', border: 'none',
              background: C.bg1,
              transition: 'width 0.2s ease',
            },
          }}
          open>
          {renderDrawerContent(collapsed)}
        </Drawer>
      </Box>

      {/* Main */}
      <Box component="main" sx={{
        flexGrow: 1, minHeight: '100vh',
        width: { xs: '100%', sm: `calc(100% - ${W}px)` },
        maxWidth: '100vw',
        transition: 'width 0.2s ease',
        background: C.bg0,
        position: 'relative', zIndex: 1,
      }}>
        <Toolbar sx={{ minHeight: { xs: '52px !important', sm: '56px !important' } }} />
        <Container maxWidth={false} sx={{ px: { xs: 1.5, sm: 2.5, md: 3 }, py: { xs: 2, sm: 3 } }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </Container>
      </Box>
    </Box>
  );
};

export default InternalLayout;
